import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import moment, { Moment } from 'moment';
import analyticsService, { ActivityTimelineData } from '../../../../../services/analytics/AnalyticsService';
import { ActivityTypeColors } from '../../../../../config/constants';
import PortalCard from '../../../../../components/PortalCard';

export type DateRange = [Moment, Moment];

interface ConversionChartProps {
    dateRange: DateRange;
}

const ConversionChart: React.FC<ConversionChartProps> = ({ dateRange }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [chartData, setChartData] = useState<ActivityTimelineData[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!dateRange[0] || !dateRange[1]) return;

            setLoading(true);
            try {
                const result = await analyticsService.getActivityTimeline(
                    dateRange[0].toISOString(),
                    dateRange[1].toISOString()
                );

                if (result.success && result.data) {
                    setChartData(result.data);
                }
            } catch (error) {
                console.error('Error fetching conversion data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange]);

    const formatDateLabel = (dateStr: string) => {
        const date = moment(dateStr);
        const daysDiff = dateRange[1] && dateRange[0] ? dateRange[1].diff(dateRange[0], 'days') : 7;
        
        if (daysDiff <= 1) {
            return date.format('HH:mm');
        } else if (daysDiff <= 31) {
            return date.format('MMM DD');
        } else {
            return date.format('MMM YYYY');
        }
    };

    const xAxis = chartData.map(item => formatDateLabel(item.timePeriod));
    const productViewValues = chartData.map(item => item.productViews || 0);
    const cartAddValues = chartData.map(item => item.cartAdds || 0);
    const checkoutValues = chartData.map(item => item.checkouts || 0);

    // Calculate conversion rate
    const totalProductViews = productViewValues.reduce((a, b) => a + b, 0);
    const totalCheckouts = checkoutValues.reduce((a, b) => a + b, 0);
    const conversionRate = totalProductViews > 0 ? ((totalCheckouts / totalProductViews) * 100).toFixed(2) : '0.00';

    return (
        <PortalCard
            sx={{
                p: isMobile ? 2 : 1.5,
                pb: isMobile ? 3 : 1.5,
                mb: isMobile ? 2 : 0,
                height: isMobile ? '100%' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible'
            }}
        >
            <Box sx={{ 
                mb: isMobile ? 1.5 : 1, 
                display: 'flex', 
                flexDirection: 'row',
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                gap: 1
            }}>
                <Box textAlign='left'>
                    <Typography variant={isMobile ? 'subtitle1' : 'subtitle1'} fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.25 }}>
                        Sales Funnel
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.75rem' }}>
                        Product views → Cart → Checkout
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant='caption' color='text.secondary' fontFamily='primaryFont' sx={{ fontSize: '0.65rem' }}>
                        Conversion Rate
                    </Typography>
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight='bold' color='success.main' fontFamily='primaryFont'>
                        {conversionRate}%
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: isMobile ? 220 : 120, width: '100%', position: 'relative', overflow: 'visible' }}>
                {chartData.length > 0 ? (
                    <LineChart
                        xAxis={[{ 
                            data: xAxis, 
                            scaleType: 'point',
                            tickLabelStyle: {
                                fontSize: isMobile ? 10 : 11,
                            }
                        }]}
                        series={[
                            {
                                data: productViewValues,
                                label: isMobile ? 'Products' : 'Product Views',
                                color: ActivityTypeColors.product_view,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: cartAddValues,
                                label: isMobile ? 'Cart' : 'Cart Additions',
                                color: ActivityTypeColors.cart_add,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: checkoutValues,
                                label: 'Checkouts',
                                color: ActivityTypeColors.checkout_complete,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                        ]}
                        height={isMobile ? 240 : 150}
                        margin={{ 
                            top: 5, 
                            right: isMobile ? 15 : 20, 
                            bottom: isMobile ? 60 : 45, 
                            left: isMobile ? 30 : 35 
                        }}
                        slotProps={{
                            legend: {
                                position: {
                                    vertical: 'bottom',
                                    horizontal: 'middle'
                                },
                                padding: { bottom: isMobile ? 5 : 0, top: isMobile ? 10 : 5 },
                                itemGap: isMobile ? 12 : 12,
                                itemMarkWidth: isMobile ? 10 : 8,
                                itemMarkHeight: isMobile ? 10 : 8,
                                labelStyle: {
                                    fontSize: isMobile ? 11 : 11,
                                }
                            },
                        }}
                        sx={{
                            '.MuiLineElement-root': { strokeWidth: isMobile ? 2 : 2 },
                            '.MuiMarkElement-root': { r: isMobile ? 3 : 3 },
                            '& .MuiChartsAxis-tickLabel': {
                                fontSize: isMobile ? '0.7rem' : '0.7rem',
                                fill: theme.palette.text.secondary
                            },
                            '& .MuiChartsLegend-label': {
                                fontSize: isMobile ? '0.75rem' : '0.75rem',
                            }
                        }}
                    />
                ) : (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant='body2' color='text.secondary'>
                            No conversion data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default ConversionChart;

